import api from "../axiosInstance";

// All calls run on the cookie session and are scoped to the current lab
// (lab-app-appointments-api.md). The server takes labId from the token.

const q = (params) => ({ params });
// SKU routes wrap payloads in { success, data }; everything else is bare.
const unwrap = (r) => (r.data && r.data.data !== undefined ? r.data.data : r.data);

export const apiError = (e) =>
  e?.response?.data?.error || e?.response?.data?.message || e?.message || "Something went wrong";

// ── Catalogue (§2) ────────────────────────────────────────────────────────────
export const skuApi = {
  list: (params) => api.get("/labSku", q(params)).then((r) => r.data),
  categories: (params) => api.get("/labSku/categories", q(params)).then(unwrap),
  get: (id) => api.get(`/labSku/${id}`).then(unwrap),
  create: (body) => api.post("/labSku", body).then(unwrap),
  update: (id, body) => api.put(`/labSku/${id}`, body).then(unwrap),
  toggle: (id) => api.patch(`/labSku/${id}/toggle`).then(unwrap),
  remove: (id) => api.delete(`/labSku/${id}`).then((r) => r.data),
};

// ── Appointments (§3) ─────────────────────────────────────────────────────────
export const apptApi = {
  list: (params) => api.get("/labAppointments", q(params)).then((r) => r.data),
  summary: (params) => api.get("/labAppointments/summary", q(params)).then((r) => r.data),
  lookupPatients: (params) => api.get("/search/patients", q(params)).then((r) => r.data),
  get: (id) => api.get(`/labAppointments/${id}`).then((r) => r.data),
  create: (body) => api.post("/labAppointments", body).then((r) => r.data),
  update: (id, body) => api.put(`/labAppointments/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/labAppointments/${id}`).then((r) => r.data),
  setStatus: (id, body) => api.patch(`/labAppointments/${id}/status`, body).then((r) => r.data),
  setCollector: (id, phlebotomistStaffId) =>
    api.patch(`/labAppointments/${id}/collector`, { phlebotomistStaffId }).then((r) => r.data),
  attachReports: (id, recordIds) =>
    api.patch(`/labAppointments/${id}/reports`, { recordIds }).then((r) => r.data),
  // Patient WhatsApp notifications (§3.5b). The confirmation is automatic on
  // booking / referral acceptance; this is the manual reminder or resend.
  notify: (id, body = {}) => api.post(`/labAppointments/${id}/notify`, body).then((r) => r.data),
  notifications: (id) => api.get(`/labAppointments/${id}/notifications`).then((r) => r.data),
};

// ── Invoices (§4) ─────────────────────────────────────────────────────────────
export const invoiceApi = {
  list: (params) => api.get("/labInvoices", q(params)).then((r) => r.data),
  summary: (params) => api.get("/labInvoices/summary", q(params)).then((r) => r.data),
  get: (id) => api.get(`/labInvoices/${id}`).then((r) => r.data),
  fromAppointment: (appointmentId, body = {}) =>
    api.post(`/labInvoices/from-appointment/${appointmentId}`, body).then((r) => r.data),
  create: (body) => api.post("/labInvoices", body).then((r) => r.data),
  update: (id, body) => api.put(`/labInvoices/${id}`, body).then((r) => r.data),
  addPayment: (id, body) => api.post(`/labInvoices/${id}/payment`, body).then((r) => r.data),
  voidPayment: (id, paymentId, reason) =>
    api.post(`/labInvoices/${id}/payment/${paymentId}/void`, { reason }).then((r) => r.data),
  cancel: (id, reason) => api.post(`/labInvoices/${id}/cancel`, { reason }).then((r) => r.data),
  remove: (id) => api.delete(`/labInvoices/${id}`).then((r) => r.data),
};

// ── Patients (existing route, used when lookup finds nobody) ─────────────────
export const patientApi = {
  create: (body) => api.post("/patients", body).then((r) => r.data),
};

// ── Reports ───────────────────────────────────────────────────────────────────
// Pathology / Radiology records live on the MHR service. The lab attaches the
// resulting record ids to the appointment with apptApi.attachReports.
export const reportApi = {
  createPathology: (body) => api.post("/mhr/pathologyRecords", body).then((r) => r.data),
  // Radiology creation is multipart: the DICOM (.dcm / .zip) plus ids.
  createRadiology: (formData) =>
    api
      .post("/radiologyRecords", formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data),
  updateRadiology: (id, body) => api.put(`/radiologyRecords/${id}`, body).then((r) => r.data),
  get: (id) => api.get(`/mhr/${id}`).then((r) => r.data),
};

// ── Test templates (results doc §2) ──────────────────────────────────────────
export const templateApi = {
  list: (params) => api.get("/labTestTemplates", q(params)).then((r) => r.data),
  get: (id) => api.get(`/labTestTemplates/${id}`).then((r) => r.data),
  preview: (id, patientId) => api.get(`/labTestTemplates/${id}/preview`, q({ patientId })).then((r) => r.data),
  fromCatalogue: (body) => api.post("/labTestTemplates/from-catalogue", body).then((r) => r.data),
  create: (body) => api.post("/labTestTemplates", body).then((r) => r.data),
  update: (id, body) => api.put(`/labTestTemplates/${id}`, body).then((r) => r.data),
  toggle: (id) => api.patch(`/labTestTemplates/${id}/toggle`).then((r) => r.data),
  linkSku: (id, labSkuId) => api.post(`/labTestTemplates/${id}/link-sku`, { labSkuId }).then((r) => r.data),
  unlinkSku: (id, labSkuId) => api.delete(`/labTestTemplates/${id}/link-sku/${labSkuId}`).then((r) => r.data),
  remove: (id) => api.delete(`/labTestTemplates/${id}`).then((r) => r.data),
};

// ── Results (results doc §3) ─────────────────────────────────────────────────
export const resultApi = {
  list: (params) => api.get("/labResults", q(params)).then((r) => r.data),
  get: (id) => api.get(`/labResults/${id}`).then((r) => r.data),
  init: (appointmentId) => api.post(`/labResults/init/${appointmentId}`).then((r) => r.data),
  setValues: (id, body) => api.put(`/labResults/${id}/values`, body).then((r) => r.data),
  verify: (id, body) => api.post(`/labResults/${id}/verify`, body).then((r) => r.data),
  unverify: (id, reason) => api.post(`/labResults/${id}/unverify`, { reason }).then((r) => r.data),
  publish: (id) => api.post(`/labResults/${id}/publish`).then((r) => r.data),
  cancel: (id, reason) => api.post(`/labResults/${id}/cancel`, { reason }).then((r) => r.data),
  trend: (params) => api.get("/labResults/trend", q(params)).then((r) => r.data),
};

// ── Global test dictionary (shared by all labs) ──────────────────────────────
export const dictionaryApi = {
  searchTests: (searchQuery) => api.get("/testName/search/testname", q({ searchQuery })).then((r) => r.data),
  searchPanels: (searchQuery) => api.get("/testpanel/search", q({ searchQuery })).then((r) => r.data),
};

const BASE = process.env.REACT_APP_BASE_URL;
const qs = (params = {}) => {
  const s = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return s ? `?${s}` : "";
};

// ── Members & logins (staff doc §1) ──────────────────────────────────────────
export const staffApi = {
  list: (params) => api.get("/labStaff", q(params)).then((r) => r.data),
  get: (staffId) => api.get(`/labStaff/${staffId}`).then((r) => r.data),
  create: (body) => api.post("/labStaff", body).then((r) => r.data),
  addExisting: (body) => api.post("/labStaff/add-existing", body).then((r) => r.data),
  update: (staffId, body) => api.put(`/labStaff/${staffId}`, body).then((r) => r.data),
  setRole: (staffId, role) => api.patch(`/labStaff/${staffId}/role`, { role }).then((r) => r.data),
  setActive: (staffId, isActive) => api.patch(`/labStaff/${staffId}/active`, { isActive }).then((r) => r.data),
  resetPassword: (staffId, password) => api.post(`/labStaff/${staffId}/reset-password`, { password }).then((r) => r.data),
  uploadSignature: (staffId, image) => api.post(`/labStaff/${staffId}/signature`, { image }).then((r) => r.data),
  signatureUrl: (staffId, bust) => `${BASE}/labStaff/${staffId}/signature${bust ? `?v=${bust}` : ""}`,
  remove: (staffId) => api.delete(`/labStaff/${staffId}`).then((r) => r.data),
};

// ── Branding / letterhead (staff doc §2) ─────────────────────────────────────
export const brandingApi = {
  get: () => api.get("/lab/branding").then((r) => r.data),
  update: (body) => api.patch("/lab/branding", body).then((r) => r.data),
  uploadLogo: (image) => api.post("/lab/branding/logo", { image }).then((r) => r.data),
  logoUrl: (bust) => `${BASE}/lab/branding/logo${bust ? `?v=${bust}` : ""}`,
};

// ── Printing (staff doc §3–§4). PDFs are opened by navigation so the cookie
// travels with the request; only status/attach go through axios.
export const printApi = {
  reportStatus: (appointmentId) => api.get(`/labReports/appointment/${appointmentId}/status`).then((r) => r.data),
  reportPdfUrl: (appointmentId, opts = {}) => `${BASE}/labReports/appointment/${appointmentId}/pdf${qs({ layout: opts.layout, download: opts.download ? 1 : undefined })}`,
  resultPdfUrl: (resultId, opts = {}) => `${BASE}/labReports/result/${resultId}/pdf${qs({ layout: opts.layout, download: opts.download ? 1 : undefined })}`,
  invoicePdfUrl: (invoiceId, opts = {}) => `${BASE}/labInvoices/${invoiceId}/pdf${qs({ layout: opts.layout, download: opts.download ? 1 : undefined })}`,
  attachReport: (appointmentId) => api.post(`/labReports/appointment/${appointmentId}/attach`).then((r) => r.data),
  // WhatsApp delivery of the current report PDF (partial or final).
  // `phone` overrides the patient's number for this send only.
  sendReport: (appointmentId, phone) =>
    api.post(`/labReports/appointment/${appointmentId}/send`, phone ? { phone } : {}).then((r) => r.data),
  deliveries: (appointmentId) => api.get(`/labReports/appointment/${appointmentId}/deliveries`).then((r) => r.data),
};

// ── Specimens / sample ID (specimens guide) ──────────────────────────────────
// Tubes carry pre-printed stickers that are scanned at collection; the first
// scan of a booking assigns its accession number (sampleId).
export const specimenApi = {
  get: (appointmentId) => api.get(`/labAppointments/${appointmentId}/specimens`).then((r) => r.data),
  // scans: [{ code: "E", label: "DL000123", manual?: true }]
  collect: (appointmentId, scans, collectedAt) =>
    api.post(`/labAppointments/${appointmentId}/specimens/collect`, { scans, ...(collectedAt ? { collectedAt } : {}) }).then((r) => r.data),
  reject: (appointmentId, code, reason) => api.post(`/labAppointments/${appointmentId}/specimens/${code}/reject`, { reason }).then((r) => r.data),
  recollect: (appointmentId, code, body) => api.post(`/labAppointments/${appointmentId}/specimens/${code}/recollect`, body).then((r) => r.data),
};

// ── Sticker rolls (label stock) ──────────────────────────────────────────────
export const labelBatchApi = {
  list: () => api.get("/labLabelBatches").then((r) => r.data),
  create: (body) => api.post("/labLabelBatches", body).then((r) => r.data),
  update: (id, body) => api.patch(`/labLabelBatches/${id}`, body).then((r) => r.data),
  lookup: (code) => api.get("/labLabelBatches/lookup", q({ code })).then((r) => r.data),
  // Print sticker stock from a roll's codes. `plan` previews the run (grid,
  // count, pages, codes already used and skipped); the PDF is the same run.
  stickerPlan: (id, opts) => api.get(`/labLabelBatches/${id}/stickers/plan`, q(opts)).then((r) => r.data),
  stickerPdfUrl: (id, opts = {}) => `${BASE}/labLabelBatches/${id}/stickers/pdf${qs(opts)}`,
};

// ── Radiology templates (radiology doc §2) ───────────────────────────────────
export const radTemplateApi = {
  catalogue: (params) => api.get("/labRadiologyTemplates/catalogue", q(params)).then((r) => r.data),
  modalities: () => api.get("/labRadiologyTemplates/modalities").then((r) => r.data),
  list: (params) => api.get("/labRadiologyTemplates", q(params)).then((r) => r.data),
  get: (id) => api.get(`/labRadiologyTemplates/${id}`).then((r) => r.data),
  fromCatalogue: (body) => api.post("/labRadiologyTemplates/from-catalogue", body).then((r) => r.data),
  create: (body) => api.post("/labRadiologyTemplates", body).then((r) => r.data),
  update: (id, body) => api.put(`/labRadiologyTemplates/${id}`, body).then((r) => r.data),
  toggle: (id) => api.patch(`/labRadiologyTemplates/${id}/toggle`).then((r) => r.data),
  linkSku: (id, labSkuId) => api.post(`/labRadiologyTemplates/${id}/link-sku`, { labSkuId }).then((r) => r.data),
  unlinkSku: (id, labSkuId) => api.delete(`/labRadiologyTemplates/${id}/link-sku/${labSkuId}`).then((r) => r.data),
  remove: (id) => api.delete(`/labRadiologyTemplates/${id}`).then((r) => r.data),
};

// ── Radiology studies (radiology doc §3) ─────────────────────────────────────
export const radStudyApi = {
  list: (params) => api.get("/labRadiologyStudies", q(params)).then((r) => ({ data: r.data, total: Number(r.headers?.["x-total-count"]) || undefined })),
  get: (id) => api.get(`/labRadiologyStudies/${id}`).then((r) => r.data),
  byAppointment: (appointmentId) => api.get(`/labAppointments/${appointmentId}/studies`).then((r) => r.data),
  init: (appointmentId) => api.post(`/labRadiologyStudies/init/${appointmentId}`).then((r) => r.data),
  acquired: (id, body) => api.post(`/labRadiologyStudies/${id}/acquired`, body).then((r) => r.data),
  // One .dcm or a .zip per series. `onProgress` gets 0–100 for a real progress bar.
  uploadDicom: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/labRadiologyStudies/${id}/dicom`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => r.data);
  },
  series: (id) => api.get(`/labRadiologyStudies/${id}/series`).then((r) => r.data),
  viewer: (id) => api.get(`/labRadiologyStudies/${id}/viewer`).then((r) => r.data),
  setKeyImages: (id, images) => api.post(`/labRadiologyStudies/${id}/keyImages`, { images }).then((r) => r.data),
  saveReport: (id, body) => api.put(`/labRadiologyStudies/${id}/report`, body).then((r) => r.data),
  verify: (id, body = {}) => api.post(`/labRadiologyStudies/${id}/verify`, body).then((r) => r.data),
  unverify: (id, reason) => api.post(`/labRadiologyStudies/${id}/unverify`, { reason }).then((r) => r.data),
  publish: (id) => api.post(`/labRadiologyStudies/${id}/publish`).then((r) => r.data),
  addendum: (id, text) => api.post(`/labRadiologyStudies/${id}/addendum`, { text }).then((r) => r.data),
  cancel: (id, reason) => api.post(`/labRadiologyStudies/${id}/cancel`, { reason }).then((r) => r.data),
};

// ── Radiology report PDF / delivery (radiology doc §4) ───────────────────────
export const radReportApi = {
  pdfUrl: (studyId, opts = {}) => `${BASE}/labReports/radiology/${studyId}/pdf${qs({ layout: opts.layout, download: opts.download ? 1 : undefined })}`,
  attach: (studyId) => api.post(`/labReports/radiology/${studyId}/attach`).then((r) => r.data),
  send: (studyId, phone) => api.post(`/labReports/radiology/${studyId}/send`, phone ? { phone } : {}).then((r) => r.data),
};
